import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Users } from 'lucide-react';

interface Subscriber {
  id: string;
  email: string;
}

interface SubscriberListProps {
  monthlySubscribers: Subscriber[];
  annualSubscribers: Subscriber[];
  totalSubscribers: number;
  hasMonthlyPlans: boolean;
  hasAnnualPlans: boolean;
}

const SubscriberList: React.FC<SubscriberListProps> = ({
  monthlySubscribers,
  annualSubscribers,
  totalSubscribers,
  hasMonthlyPlans,
  hasAnnualPlans,
}) => {
  const [activeTab, setActiveTab] = useState<string>(hasMonthlyPlans ? 'monthly' : 'annual');

  const renderSubscriberTable = (subscribers: Subscriber[], label: string) => {
    if (subscribers.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground text-sm">
          No {label.toLowerCase()} subscribers found.
        </div>
      );
    }

    return (
      <>
        {/* Mobile Card View */}
        <div className="md:hidden space-y-3">
          {subscribers.map((subscriber, index) => (
            <Card key={subscriber.id} className="p-3">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Badge variant="secondary" className="text-xs">
                    #{index + 1}
                  </Badge>
                  <span className="text-xs text-muted-foreground font-mono truncate max-w-[200px]">
                    {subscriber.id}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Email</p>
                  <p className="text-sm font-medium break-all">{subscriber.email}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">#</TableHead>
                <TableHead>User ID</TableHead>
                <TableHead>Email</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscribers.map((subscriber, index) => (
                <TableRow key={subscriber.id}>
                  <TableCell className="font-medium">{index + 1}</TableCell>
                  <TableCell className="font-mono text-sm">{subscriber.id}</TableCell>
                  <TableCell>{subscriber.email}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </>
    );
  };

  return (
    <Card>
      <CardHeader className="p-3 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0 flex-1">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Users className="h-4 w-4 sm:h-5 sm:w-5" />
              Subscribers
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm mt-1">
              Users currently subscribed to this plan
            </CardDescription>
          </div>
          <Badge variant="secondary" className="text-sm sm:text-lg px-2 py-1 sm:px-3 self-start sm:self-auto shrink-0">
            {totalSubscribers} total
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-3 sm:p-6">
        {!hasMonthlyPlans && !hasAnnualPlans ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            No plans configured for this subscription.
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 gap-1 sm:gap-0 max-w-md h-auto p-1">
              <TabsTrigger value="monthly" disabled={!hasMonthlyPlans} className="text-xs sm:text-sm px-2 py-2 sm:px-3">
                Monthly ({monthlySubscribers.length})
              </TabsTrigger>
              <TabsTrigger value="annual" disabled={!hasAnnualPlans} className="text-xs sm:text-sm px-2 py-2 sm:px-3">
                Annual ({annualSubscribers.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="monthly" className="mt-3 sm:mt-4">
              {renderSubscriberTable(monthlySubscribers, 'Monthly')}
            </TabsContent>

            <TabsContent value="annual" className="mt-3 sm:mt-4">
              {renderSubscriberTable(annualSubscribers, 'Annual')}
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
};

export default SubscriberList;
